import * as React from "react"
import { useState } from "react"
import Avatar from "@imminently/decisively-ui/Avatar"
import AvatarGroup from "@imminently/decisively-ui/AvatarGroup"
import Switch from "@imminently/decisively-ui/Switch"
import { Label } from "@/ui/primitives/label"
import Select from "@imminently/decisively-ui/Select"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage"

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl"

const SAMPLE_USERS = [
  { name: "Alice Johnson", src: undefined },
  { name: "Bob Smith", src: undefined },
  { name: "Carol White", src: undefined },
  { name: "David Brown", src: undefined },
  { name: "Eve Davis", src: undefined },
  { name: "Frank Miller", src: undefined },
]

export function AvatarPage() {
  const [size, setSize] = useState<AvatarSize>("lg")
  const [invert, setInvert] = useState(false)
  const [mono, setMono] = useState(false)
  const [withImage, setWithImage] = useState(false)
  const [groupMax, setGroupMax] = useState("4")

  return (
    <ComponentPage
      title="Avatar"
      description="An image element with a fallback for representing the user."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with different avatar configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Avatar
              size={size}
              invert={invert}
              mono={mono}
              name="Alice Johnson"
              src={withImage ? "https://i.pravatar.cc/150?img=1" : undefined}
              alt="Alice Johnson"
            />
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Size">
              <Select
                value={size}
                onValueChange={(v) => setSize((v ?? "lg") as AvatarSize)}
                options={[
                  { value: "xs", label: "XS" },
                  { value: "sm", label: "SM" },
                  { value: "md", label: "MD" },
                  { value: "lg", label: "LG" },
                  { value: "xl", label: "XL" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Invert">
              <Switch checked={invert} onCheckedChange={setInvert} />
            </ControlRow>
            <ControlRow label="Mono (grayscale img)">
              <Switch checked={mono} onCheckedChange={setMono} />
            </ControlRow>
            <ControlRow label="With Image">
              <Switch checked={withImage} onCheckedChange={setWithImage} />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* Sizes */}
      <ComponentSection title="Sizes" description="Available avatar sizes">
        <ComponentPreview className="gap-4 items-end">
          <VariantGrid columns={5}>
            {(["xs", "sm", "md", "lg", "xl"] as AvatarSize[]).map((s) => (
              <VariantItem key={s} label={s.toUpperCase()}>
                <Avatar size={s} name="Alice Johnson" />
              </VariantItem>
            ))}
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* Variants */}
      <ComponentSection title="Variants" description="Default and inverted styles">
        <ComponentPreview className="gap-8">
          <VariantGrid columns={2}>
            <VariantItem label="Default">
              <Avatar name="Alice Johnson" />
            </VariantItem>
            <VariantItem label="Invert">
              <Avatar name="Alice Johnson" invert />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* With Image */}
      <ComponentSection title="With Image" description="Avatar with an image source">
        <ComponentPreview className="gap-4">
          <VariantGrid columns={5}>
            {(["xs", "sm", "md", "lg", "xl"] as AvatarSize[]).map((s, i) => (
              <VariantItem key={s} label={s.toUpperCase()}>
                <Avatar
                  size={s}
                  src={`https://i.pravatar.cc/150?img=${i + 1}`}
                  alt="User"
                />
              </VariantItem>
            ))}
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* Fallback behaviour */}
      <ComponentSection title="Fallback" description="Initials derived from name or alt, or custom children">
        <ComponentPreview className="gap-4">
          <VariantGrid columns={4}>
            <VariantItem label="name prop">
              <Avatar name="Alice Johnson" />
            </VariantItem>
            <VariantItem label="alt prop">
              <Avatar alt="Bob Smith" />
            </VariantItem>
            <VariantItem label="single word">
              <Avatar name="Carol" />
            </VariantItem>
            <VariantItem label="custom children">
              <Avatar>?</Avatar>
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* Mono */}
      <ComponentSection title="Mono" description="Grayscale filter applied to image avatars">
        <ComponentPreview className="gap-8">
          <VariantGrid columns={2}>
            <VariantItem label="Normal">
              <Avatar src="https://i.pravatar.cc/150?img=5" alt="User" size="xl" />
            </VariantItem>
            <VariantItem label="Mono">
              <Avatar src="https://i.pravatar.cc/150?img=5" alt="User" size="xl" mono />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* Avatar Group Playground */}
      <ComponentSection
        title="Avatar Group"
        description="Overlapping avatars with overflow count"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <AvatarGroup max={Number(groupMax)} size="md">
              {SAMPLE_USERS.map((u) => (
                <Avatar key={u.name} name={u.name} size="md" />
              ))}
            </AvatarGroup>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Max visible">
              <Select
                value={groupMax}
                onValueChange={(v) => setGroupMax(v ?? "4")}
                options={[2, 3, 4, 5, 6].map((n) => ({ value: String(n), label: String(n) }))}
              />
            </ControlRow>
            <ControlRow label="Total avatars">
              <span className="text-sm text-muted-foreground">{SAMPLE_USERS.length}</span>
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* Avatar Group Sizes */}
      <ComponentSection title="Group Sizes" description="Avatar groups at different sizes">
        <ComponentPreview className="flex-col gap-6 items-start">
          {(["xs", "sm", "md", "lg", "xl"] as AvatarSize[]).map((s) => (
            <div key={s} className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-6">{s.toUpperCase()}</span>
              <AvatarGroup max={4} size={s}>
                {SAMPLE_USERS.map((u) => (
                  <Avatar key={u.name} name={u.name} size={s} />
                ))}
              </AvatarGroup>
            </div>
          ))}
        </ComponentPreview>
      </ComponentSection>

      {/* Group Spacing */}
      <ComponentSection title="Group Spacing" description="Compact vs default spacing">
        <ComponentPreview className="flex-col gap-6 items-start">
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground w-20">Medium</span>
            <AvatarGroup max={5} spacing="medium" size="md">
              {SAMPLE_USERS.slice(0, 5).map((u) => (
                <Avatar key={u.name} name={u.name} size="md" />
              ))}
            </AvatarGroup>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground w-20">Small</span>
            <AvatarGroup max={5} spacing="small" size="md">
              {SAMPLE_USERS.slice(0, 5).map((u) => (
                <Avatar key={u.name} name={u.name} size="md" />
              ))}
            </AvatarGroup>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Usage example */}
      <ComponentSection title="Usage Example" description="Avatars in a realistic context">
        <ComponentPreview>
          <div className="w-full max-w-sm rounded-lg border p-4 space-y-4">
            <div className="flex items-center gap-3">
              <Avatar src="https://i.pravatar.cc/150?img=3" alt="Sarah Connor" size="md" />
              <div>
                <p className="text-sm font-medium">Sarah Connor</p>
                <p className="text-xs text-muted-foreground">Project Owner</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Team</span>
              <AvatarGroup max={3} size="sm">
                {SAMPLE_USERS.map((u) => (
                  <Avatar key={u.name} name={u.name} size="sm" />
                ))}
              </AvatarGroup>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
